import api from "../../api";
import {Element, scroller} from 'react-scroll'
import React, {useEffect, useState, useCallback, useRef, useMemo} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {
    Alert,
    Button,
    Checkbox,
    Col,
    DatePicker,
    Drawer,
    Form, Input, Modal,
    Row,
    Select,
    Spin,
    Tabs,
    Tooltip
} from "antd";
import {DndContext, useSensor, useSensors, PointerSensor, useDroppable, DragOverlay, closestCenter, pointerWithin, rectIntersection} from '@dnd-kit/core';
import {SortableContext, verticalListSortingStrategy, arrayMove, useSortable} from '@dnd-kit/sortable';
import {CSS} from '@dnd-kit/utilities';
import PlantingModal from "../../Components/PlantingModal";
import BedModal from "../../Components/BedModal";
import dayjs from "dayjs";
import "./style.css"
import {
    GroupOutlined,
    OrderedListOutlined,
    PlusOutlined,
    SettingFilled,
    WarningOutlined
} from "@ant-design/icons";
import {PLANTING_STATUS, PLANTING_STATUS_LABELS} from "../../constants";

import {bedDimensionsToHuman, filterPlanting, calculateRowsAndPlants} from "../../helpers";
import {isMobile} from "react-device-detect";
import HelpVideoLoader from "../../Components/HelpVideoLoader";
import {useTranslations} from "../../lang";
import {useAuth} from "../../auth";
import BedOrder from "../../Components/BedOrder";
import CropImage from "../../Components/CropImage";
import PlantingDrawer from "../../Components/PlantingDrawer";

// Sortable planting component (replaces DraggablePlanting)
function SortablePlanting({planting, bed, children}) {
    const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({
        id: `planting-${planting.id}`,
        data: {
            planting: planting,
            sourceBedId: bed.id,
            type: 'planting'
        }
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab'
    };

    return (
        <div ref={setNodeRef} style={style} {...listeners} {...attributes}>
            {children}
        </div>
    );
}

// Droppable bed component
function DroppableBed({bed, children}) {
    const {setNodeRef, isOver} = useDroppable({
        id: `bed-${bed.id}`,
        data: {
            bed: bed,
            type: 'bed'
        }
    });

    const style = {
        backgroundColor: isOver ? '#e6f7ff' : 'transparent',
        border: isOver ? '2px solid #1890ff' : '2px solid transparent',
        borderRadius: isOver ? '8px' : '0',
        padding: isOver ? '8px' : '0',
        transition: 'all 0.2s ease',
        boxShadow: isOver ? '0 4px 12px rgba(24, 144, 255, 0.2)' : 'none'
    };

    return (
        <div ref={setNodeRef} style={style}>
            {children}
        </div>
    );
}

export default function PlannerV2() {

    const {translate} = useTranslations()
    const {measurementSystem} = useAuth()

    const groupByPeriodLocal = localStorage.getItem('groupByPeriod')
    const hideHarvestedLocal = localStorage.getItem('hideHarvested')
    const compactModeLocal = localStorage.getItem('compactMode')
    const defaultDateRangeLocal = localStorage.getItem('defaultDateRange')
    const hideBedDimensionsLocal = localStorage.getItem('hideBedDimensions')
    const hideCropVarietiesLocal = localStorage.getItem('hideCropVarieties')
    const amountDisplayLocal = localStorage.getItem('amountDisplay')

    const [defaultDateRange, setDefaultDateRange] = useState(defaultDateRangeLocal ?? "next_12_months")
    const [groupByPeriod, setGroupByPeriod] = useState(groupByPeriodLocal ?? "month")
    const [hideHarvested, setHideHarvested] = useState(hideHarvestedLocal ? hideHarvestedLocal === "true" : true)
    const [compactMode, setCompactMode] = useState(compactModeLocal ? compactModeLocal === "true" : false)
    const [hideBedDimensions, setHideBedDimensions] = useState(hideBedDimensionsLocal ? hideBedDimensionsLocal === "true" : false)
    const [hideCropVarieties, setHideCropVarieties] = useState(hideCropVarietiesLocal ? hideCropVarietiesLocal === "true" : true)
    const [amountDisplay, setAmountDisplayState] = useState(amountDisplayLocal ?? "total")

    const navigate = useNavigate();

    const [searchParams] = useSearchParams();

    let {id} = useParams();
    const [garden, setGarden] = useState(null)
    const [plantingModalOpen, setPlantingModalOpen] = useState(false)
    const [selectedBed, setSelectedBed] = useState(null)
    const [selectedPlanting, setSelectedPlanting] = useState(null)
    const [bedModalOpen, setBedModalOpen] = useState(false)
    const [allGardens, setAllGardens] = useState([])
    const [showDisplayDrawer, setShowDisplayDrawer] = useState(false)
    const [showSortingDrawer, setShowSortingDrawer] = useState(false)
    const [showPlantingDrawer, setShowPlantingDrawer] = useState(false)
    const [startDate, setStartDate] = useState(dayjs().startOf('month'))
    const [endDate, setEndDate] = useState(dayjs().endOf('year'))
    const [periods, setPeriods] = useState([])
    const [overdueTasks, setOverdueTasks] = useState([])
    const [search, setSearch] = useState("")
    const [showCustomDateModal, setShowCustomDateModal] = useState(false)
    const [customDateRange, setCustomDateRange] = useState([null, null])
    const [activeDragPlanting, setActiveDragPlanting] = useState(null)

    // Ref to always hold the latest garden state (avoids stale closures in drag handlers)
    const gardenRef = useRef(garden);
    useEffect(() => {
        gardenRef.current = garden;
    }, [garden]);

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor, {
            activationConstraint: {
                distance: 8, // Minimum drag distance before activation
            },
        })
    );

    // Helper: find which bed contains a given planting sortable id
    // Uses gardenRef.current to avoid stale closure issues in drag handlers
    function findBedByPlantingId(plantingDndId) {
        const g = gardenRef.current;
        if (!g) return null;
        const plantingId = parseInt(plantingDndId.replace('planting-', ''));
        return g.beds.find(bed =>
            bed.plantings.some(p => p.id === plantingId)
        );
    }

    // Helper: find planting object by dnd id
    function findPlantingByDndId(dndId) {
        const g = gardenRef.current;
        if (!g) return null;
        const plantingId = parseInt(dndId.replace('planting-', ''));
        for (const bed of g.beds) {
            const planting = bed.plantings.find(p => p.id === plantingId);
            if (planting) return planting;
        }
        return null;
    }

    // Custom collision detection: prioritize planting items over bed containers
    function customCollisionDetection(args) {
        const pointerCollisions = pointerWithin(args);
        if (pointerCollisions.length > 0) {
            // Prefer planting items over bed droppable areas
            const plantingCollisions = pointerCollisions.filter(c => String(c.id).startsWith('planting-'));
            if (plantingCollisions.length > 0) {
                // Among planting collisions, use closestCenter to pick the best one
                const plantingIds = new Set(plantingCollisions.map(c => c.id));
                const filteredArgs = {
                    ...args,
                    droppableContainers: args.droppableContainers.filter(c => plantingIds.has(c.id))
                };
                return closestCenter(filteredArgs);
            }
            return pointerCollisions;
        }
        // Fall back to rect intersection (catches bed droppable areas for empty beds)
        return rectIntersection(args);
    }

    function handleDragStart(event) {
        const {active} = event;
        const planting = active.data.current?.planting;
        if (planting) {
            setActiveDragPlanting(planting);
        }
    }

    // Handle drag over - move planting between beds in local state for visual feedback
    function handleDragOver(event) {
        const {active, over} = event;
        if (!over || !gardenRef.current) return;

        const activeId = active.id;
        const overId = over.id;

        // Find source bed
        const sourceBed = findBedByPlantingId(activeId);
        if (!sourceBed) return;

        // Determine target bed
        let targetBed = null;
        if (String(overId).startsWith('bed-')) {
            const targetBedId = parseInt(String(overId).replace('bed-', ''));
            targetBed = gardenRef.current.beds.find(b => b.id === targetBedId);
        } else if (String(overId).startsWith('planting-')) {
            targetBed = findBedByPlantingId(overId);
        }

        if (!targetBed || sourceBed.id === targetBed.id) return;

        // Move planting from source to target bed in local state
        const plantingId = parseInt(String(activeId).replace('planting-', ''));
        const planting = sourceBed.plantings.find(p => p.id === plantingId);
        if (!planting) return;

        setGarden(prev => {
            const newBeds = prev.beds.map(bed => {
                if (bed.id === sourceBed.id) {
                    return {...bed, plantings: bed.plantings.filter(p => p.id !== plantingId)};
                }
                if (bed.id === targetBed.id) {
                    // Insert at the position of the target planting, or at end
                    const newPlantings = [...bed.plantings];
                    if (String(overId).startsWith('planting-')) {
                        const overPlantingId = parseInt(String(overId).replace('planting-', ''));
                        const overIndex = newPlantings.findIndex(p => p.id === overPlantingId);
                        if (overIndex >= 0) {
                            newPlantings.splice(overIndex, 0, planting);
                        } else {
                            newPlantings.push(planting);
                        }
                    } else {
                        newPlantings.push(planting);
                    }
                    return {...bed, plantings: newPlantings};
                }
                return bed;
            });
            return {...prev, beds: newBeds};
        });
    }

    // Handle drag end - persist changes
    function handleDragEnd(event) {
        const {active, over} = event;
        setActiveDragPlanting(null);

        if (!over || !gardenRef.current) return;

        const activeId = String(active.id);
        const overId = String(over.id);

        // Find source bed (where the planting currently is after any dragOver moves)
        const currentBed = findBedByPlantingId(activeId);
        if (!currentBed) return;

        const sourceBedId = active.data.current?.sourceBedId;
        const plantingId = parseInt(activeId.replace('planting-', ''));
        const isCrossBedMove = sourceBedId && currentBed.id !== sourceBedId;

        // Cross-bed move: planting was already moved visually in handleDragOver.
        // Check this FIRST, because after handleDragOver the planting is already
        // in the target bed, so same-bed reorder logic would incorrectly match.
        if (isCrossBedMove) {
            const planting = active.data.current?.planting;
            if (!planting) return;

            const targetBed = currentBed;
            const calculated = calculateRowsAndPlants(targetBed, planting.crop);

            // Persist the bed move, then update planting order for both beds
            api.updatePlanting(planting.id, {
                bed_id: targetBed.id,
                rows: calculated.rows,
                plants_per_row: calculated.plants_per_row
            }).then(() => {
                const g = gardenRef.current;
                const orderPromises = [];
                // Persist order for target bed
                const updatedTargetBed = g.beds.find(b => b.id === targetBed.id);
                if (updatedTargetBed) {
                    orderPromises.push(api.savePlantingOrder(g.id, updatedTargetBed.id, updatedTargetBed.plantings.map(p => p.id)));
                }
                // Persist order for source bed
                const srcBed = g.beds.find(b => b.id === sourceBedId);
                if (srcBed) {
                    orderPromises.push(api.savePlantingOrder(g.id, srcBed.id, srcBed.plantings.map(p => p.id)));
                }
                // Wait for order updates to persist before reloading
                Promise.all(orderPromises).then(() => loadGarden());
            });
            return;
        }

        // Same-bed reorder: only when dropped on a different planting in the same bed
        if (overId.startsWith('planting-')) {
            const overPlantingId = parseInt(overId.replace('planting-', ''));
            const overBed = findBedByPlantingId(overId);

            if (overBed && overBed.id === currentBed.id && plantingId !== overPlantingId) {
                const oldIndex = currentBed.plantings.findIndex(p => p.id === plantingId);
                const newIndex = currentBed.plantings.findIndex(p => p.id === overPlantingId);

                if (oldIndex !== -1 && newIndex !== -1 && oldIndex !== newIndex) {
                    const newPlantings = arrayMove(currentBed.plantings, oldIndex, newIndex);
                    setGarden(prev => ({
                        ...prev,
                        beds: prev.beds.map(bed =>
                            bed.id === currentBed.id ? {...bed, plantings: newPlantings} : bed
                        )
                    }));
                    // Persist the new order
                    api.savePlantingOrder(gardenRef.current.id, currentBed.id, newPlantings.map(p => p.id));
                    return;
                }
            }
        }

        // Same bed, same position or dropped on bed container - persist current order
        if (currentBed) {
            api.savePlantingOrder(gardenRef.current.id, currentBed.id, currentBed.plantings.map(p => p.id));
        }
    }

    function openModal(bed) {
        setSelectedBed(bed)
        setBedModalOpen(true)
    }

    function loadGarden() {
        api.garden(id).then(setGarden)
        api.gardens().then(setAllGardens)
        setPreSelectedDateRange(defaultDateRange)
        api.getTasks({
            garden_id: id,
            is_completed: false,
            end_date: dayjs().endOf('day').format("YYYY-MM-DD")}
        ).then(setOverdueTasks)
        if (groupByPeriod === "month" || groupByPeriod === "week") {
            setPeriods(generatePeriods(startDate, endDate, groupByPeriod))
        }
    }

    function showPlantingModal(bed) {
        setSelectedBed(bed)
        setPlantingModalOpen(true)
    }

    function onClose() {
        setPlantingModalOpen(false);
        setBedModalOpen(false);
        setSelectedPlanting(null);
        setSelectedBed(null);
        loadGarden();
    }

    function generatePeriods(start, end, groupBy) {
        let current = start;
        if (groupBy === "text" || groupBy === "none") {
            return []
        }
        const groups = [];

        // Helper function to determine next grouping point
        const getNextGroupStart = (currentDate, groupBy) => {
            if (groupBy === 'week') {
                return currentDate.startOf('week').add(1, 'week');
            } else if (groupBy === 'month') {
                return currentDate.startOf('month').add(1, 'month');
            } else if (groupBy === 'year') {
                return currentDate.startOf('year').add(1, 'year');
            } else {
                throw new Error("Invalid groupBy value. Use 'week' or 'month'.");
            }
        };

        while (current.isBefore(end)) {
            const groupStart = current.startOf(groupBy);
            groups.push(groupStart);
            current = getNextGroupStart(current, groupBy);
        }

        return groups;
    }


    useEffect(() => {
        loadGarden()
    }, []);


    useEffect(() => {
        loadGarden()
    }, [id]);

    useEffect(() => {
        if (!searchParams.get('plantingId')) {
            return
        }

        if(!garden) {
            return
        }

        setTimeout(() => {
            scroller.scrollTo(`planting-${searchParams.get('plantingId')}`, {
                duration: 800,
                delay: 0,
                smooth: true,
                offset: -100 // Scrolls to element + 50 pixels down the page
            })
            garden.beds.forEach(bed => {
                bed.plantings.forEach(planting => {
                    if (planting.id === parseInt(searchParams.get('plantingId'))) {
                        setSelectedBed(bed)
                        setSelectedPlanting(planting)
                        setShowPlantingDrawer(true)
                    }
                })
            })

        }, 1000)
    }, [garden]);

    useEffect(() => {
        setPeriods(generatePeriods(startDate, endDate, groupByPeriod))
    }, [startDate, endDate, groupByPeriod]);


    if (garden === null) {
        return (<Spin/>)
    }

    const periodToHuman = (period) => {
        if (groupByPeriod === "month") {
            return period.format("MMM")
        }
        if (groupByPeriod === "week") {
            return period.format("w")
        }
        if (groupByPeriod === "year") {
            return period.format("YYYY")
        }
        return period.toString()
    }

    function inDateRange(start, end, period) {
        const periodEnd = period.endOf(groupByPeriod)
        return period.isBefore(end) && periodEnd.isAfter(start)
    }

    function inBed(planting, period) {
        return inDateRange(dayjs(planting.transplant_at), dayjs(planting.harvest_at), period)
    }

    function inSeed(planting, period) {
        return inDateRange(dayjs(planting.sow_at), dayjs(planting.transplant_at), period)
    }

    function inHarvest(planting, period) {
        return inDateRange(dayjs(planting.harvest_at), dayjs(planting.harvest_until), period)
    }

    function isNotOnTrack(planting) {
        let plantingTasks = overdueTasks.filter(task => task.planting_id === planting.id)
        if (plantingTasks.length > 0) {
            return plantingTasks[0].task
        }
        return false
    }

    function setHideBedDimensionsLocal(e) {
        const checked = e.target.checked
        localStorage.setItem('hideBedDimensions', checked)
        setHideBedDimensions(checked)
    }

    function setHideCropVarietiesLocal(e) {
        const checked = e.target.checked
        localStorage.setItem('hideCropVarieties', checked)
        setHideCropVarieties(checked)
    }

    function setHideHarvestedLocal(e) {
        const checked = e.target.checked
        localStorage.setItem('hideHarvested', checked)
        setHideHarvested(checked)
    }

    function setCompactModeLocal(e) {
        const checked = e.target.checked
        localStorage.setItem('compactMode', checked)
        setCompactMode(checked)
    }

    function setGroupBy(period) {
        localStorage.setItem('groupByPeriod', period)
        setGroupByPeriod(period)
        setPeriods(generatePeriods(startDate, endDate, period))
    }

    function setAmountDisplay(value) {
        localStorage.setItem('amountDisplay', value)
        setAmountDisplayState(value)
    }

    function setPreSelectedDateRange(selection) {
        if (selection === "custom") {
            setShowCustomDateModal(true)
            return
        }

        localStorage.setItem('defaultDateRange', selection)
        setDefaultDateRange(selection)
        if (selection === "today_end_of_year") {
            setStartDate(dayjs().startOf(groupByPeriod))
            setEndDate(dayjs().endOf('year'))
        }
        if (selection === "start_of_year_today") {
            setStartDate(dayjs().startOf('year'))
            setEndDate(dayjs())
        }
        if (selection === "this_year") {
            setStartDate(dayjs().startOf('year'))
            setEndDate(dayjs().endOf('year'))
        }
        if (selection === "last_year") {
            setStartDate(dayjs().startOf('year').subtract(1, 'year'))
            setEndDate(dayjs().endOf('year').subtract(1, 'year'))
        }
        if (selection === "next_year") {
            setStartDate(dayjs().startOf('year').add(1, 'year'))
            setEndDate(dayjs().endOf('year').add(1, 'year'))
        }
        if (selection === "next_12_months") {
            setStartDate(dayjs().startOf(groupByPeriod))
            setEndDate(dayjs().add(11, 'months'))
        }
        if (selection === "next_3_months") {
            setStartDate(dayjs().startOf(groupByPeriod))
            setEndDate(dayjs().add(3, 'months'))
        }
    }

    function handleCustomDateRangeApply() {
        if (customDateRange[0] && customDateRange[1]) {
            setStartDate(dayjs(customDateRange[0]))
            setEndDate(dayjs(customDateRange[1]))
            localStorage.setItem('defaultDateRange', 'custom')
            setDefaultDateRange('custom')
            setShowCustomDateModal(false)
        }
    }

    function navigateToPlanting(bed, planting) {
        setSelectedBed(bed)
        setSelectedPlanting(planting)
        setShowPlantingDrawer(true)
        // navigate(`/garden/${garden.id}/bed/${bed.id}/planting/${planting.id}`)
    }

    function filterBedsOnSearch(bed) {
        if(search === "") {
            return true
        }

        const plantings = bed.plantings.filter((planting) => {
            if (planting.crop.name.toLowerCase().includes(search.toLowerCase())) {
                return true
            }
            if (planting.note && planting.note.toLowerCase().includes(search.toLowerCase())) {
                return true
            }
            if (planting.crop.variety && planting.crop.variety.toLowerCase().includes(search.toLowerCase())) {
                return true
            }
            if (bed.name.toLowerCase().includes(search.toLowerCase())) {
                return true
            }
        })

        if(plantings.length > 0) {
            return true
        }

        if (bed.name.toLowerCase().includes(search.toLowerCase())) {
            return true
        }

        return false
    }

    const activeTabKey = `garden_tab_${allGardens.findIndex(g => g.id === parseInt(id))}`;
    return (
        <DndContext
            sensors={sensors}
            collisionDetection={customCollisionDetection}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
        >
            <div className={compactMode ? "planner-compact" : 'planner-large'}>
            <Tabs
                activeKey={activeTabKey}
                defaultActiveKey="garden_tab_1"
                type="card"
                size={"large"}
                style={{
                    marginBottom: 32,
                }}
                onTabClick={(key) => {
                    const id = key.replace('garden_tab_', '')
                    navigate(`/planner/${id}`)
                }}
                items={allGardens.map(g => ({
                    key: `garden_tab_${g.id}`,
                    label: g.name
                }))}
            />
            <Drawer size={"large"} open={showPlantingDrawer} onClose={() => {
                setShowPlantingDrawer(false)
            }}>
                {(garden && selectedPlanting && selectedBed) && (
                    <PlantingDrawer gardenId={garden.id}
                                    plantingId={selectedPlanting.id}
                                    bedId={selectedBed.id}
                                    onCloseDrawer={() => {
                                        setShowPlantingDrawer(false)
                                        setSelectedBed(null)
                                        setSelectedPlanting(null)
                                        // setGarden(null)
                                        loadGarden()
                                    }}/>
                )}
            </Drawer>
            <Drawer open={showSortingDrawer} onClose={() => {
                setShowSortingDrawer(false)
                loadGarden()
            }}>
                <BedOrder beds={garden.beds} gardenId={garden.id} doneSorting={() => {
                    setShowSortingDrawer(false)
                    loadGarden()
                }}></BedOrder>
            </Drawer>
            <Drawer open={showDisplayDrawer} onClose={() => setShowDisplayDrawer(false)}>
                <Form.Item label={"Weergave kalender"} layout={"vertical"}>
                    <Select size={"small"}
                            defaultValue={groupByPeriod}
                            onChange={(e) => setGroupBy(e)}
                            options={[
                                {label: translate("Niet"), value: "none"},
                                {label: translate("Als tekst"), value: "text"},
                                {label: translate("Per week"), value: "week"},
                                {label: translate("Per maand"), value: "month"},
                                // {label: "Per jaar", value: "year"},
                            ]}
                    >
                    </Select>
                </Form.Item>
                <Form.Item label={translate("Aantallen weergeven als")} layout={"vertical"}>
                    <Select size={"small"}
                            defaultValue={amountDisplay}
                            onChange={(e) => setAmountDisplay(e)}
                            options={[
                                {label: translate("Totaal"), value: "total"},
                                {label: translate("Aantal rijen en planten"), value: "rows_and_plants"},
                                {label: translate("Niet"), value: "none"},
                            ]}
                    >
                    </Select>
                </Form.Item>
                <Form.Item
                    help={translate("Als je dit aanvinkt, zal alles wat je al als geoogst hebt gemarkeerd worden verborgen.")}>
                    <Checkbox checked={hideHarvested}
                              onChange={setHideHarvestedLocal}></Checkbox> {translate('Verberg geoogst')}
                </Form.Item>
                <Form.Item
                    help={translate("Deze optie zorgt ervoordat alles compacter wordt weergegeven, dit is vooral handig als je heel veel bedden in 1 tuin hebt! Deze optie werkt alleen op desktop, niet op mobiele apparaten")}>
                    <Checkbox disabled={isMobile} checked={compactMode}
                              onChange={setCompactModeLocal}></Checkbox> {translate('Compacte modus')}
                </Form.Item>
                <Form.Item
                    help={translate("Als je dit aanvinkt worden de maten van de bedden verborgen. Dit is makkelijk als als je bedden dezelfde maat hebben.")}>
                    <Checkbox checked={hideBedDimensions}
                              onChange={setHideBedDimensionsLocal}></Checkbox> {translate('Verberg afmetingen van bedden')}
                </Form.Item>
                <Form.Item
                    help={translate("Stel, je kweekt meerdere rassen van bijvoorbeeld tomaten. Dan is het handig om te zien om welk ras het gaat. Als je dit ook wilt zien in je planner moet je dit uitvinken")}>
                    <Checkbox checked={hideCropVarieties}
                              onChange={setHideCropVarietiesLocal}></Checkbox> {translate('Verberg rassen van gewassen')}
                </Form.Item>

                <h1 className={"mar-t-2"}>{translate('Legenda')}</h1>
                <div className={"period-in-seed pad-1 mar-b-1"}>
                    {translate('Het gewas wordt voorgezaaid, op een andere plek dan in het bed')}
                </div>
                <div className={"period-in-bed pad-1 mar-b-1"}>
                    {translate('Gewas staat in de grond / neemt ruimte in het bed')}
                </div>
                <div className={"period-in-harvest pad-1 mar-b-1"}>
                    {translate('Het gewas is te oogsten')}
                </div>
            </Drawer>
            <Row className={"no-print"} gutter={[12,12]}>
                <Col xs={24} md={6}>
                    <Form.Item>
                        <Input onChange={(e) => {
                            setSearch(e.target.value)
                        }} placeholder={"Zoeken in planner op gewas of bed..."}></Input>
                    </Form.Item>
                </Col>
                <Col xs={16} md={6}>
                    <Form.Item>
                        <Select value={defaultDateRange}
                                style={{width: '100%'}}
                                onChange={(e) => setPreSelectedDateRange(e)}
                                options={[
                                    {label: translate("Vandaag tot einde jaar"), value: "today_end_of_year"},
                                    {label: translate("Begin dit jaar tot vandaag"), value: "start_of_year_today"},
                                    {label: translate("Dit jaar"), value: "this_year"},
                                    {label: translate("Vorig jaar"), value: "last_year"},
                                    {label: translate("Komende 3 maanden"), value: "next_3_months"},
                                    {label: translate("Komende 12 maanden"), value: "next_12_months"},
                                    {label: translate("Zelf selecteren"), value: "custom"}
                                ]}></Select>
                    </Form.Item>
                </Col>
                <Col xs={0} md={12} className={"text-right"}>
                    <Button onClick={() => navigate(`/map/${garden.id}`)} className={"mar-r-1"}>
                        <GroupOutlined/> {translate('Toon kaart')}
                    </Button>
                    <Button onClick={() => setShowDisplayDrawer(!showDisplayDrawer)} className={"mar-r-1"}>
                        <SettingFilled/> {translate('Weergave instellingen')}
                    </Button>
                    <Button onClick={() => setShowSortingDrawer(!showDisplayDrawer)}>
                        <OrderedListOutlined/> {translate('Volgorde bedden')}
                    </Button>
                </Col>
                <Col xs={4} md={0}>
                    <Button onClick={() => setShowDisplayDrawer(!showDisplayDrawer)}>
                        <SettingFilled/>
                    </Button>
                </Col>
                <Col xs={4} md={0}>
                    <Button onClick={() => setShowSortingDrawer(!showDisplayDrawer)}>
                        <OrderedListOutlined/>
                    </Button>
                </Col>

            </Row>

            <PlantingModal
                open={plantingModalOpen}
                onClose={onClose}
                bed={selectedBed}
                planting={selectedPlanting}
                onPlantingAdded={(planting) => {
                    if (dayjs(planting.sow_at).unix() > endDate.unix()) {
                        Modal.info({
                            title: translate('Let op!'),
                            content: translate(
                                `De aanplant is automatisch gepland in de toekomst. Je ziet nu niks in de planner, want de zaaidatum valt buiten de geselecteerde periode. `
                                + `Je kunt de tijdlijn van de planner aanpassen door (bijvoorbeeld) de weergave op de komende 12 maanden te zetten.`
                            )
                        })
                    }
                    onClose()
                }}
            ></PlantingModal>

            <BedModal garden={garden} onCancel={onClose} open={bedModalOpen} bed={selectedBed}></BedModal>

            <Modal
                title={translate("Selecteer datumbereik")}
                open={showCustomDateModal}
                onCancel={() => {
                    setShowCustomDateModal(false)
                    setDefaultDateRange(defaultDateRange === 'custom' ? 'next_12_months' : defaultDateRange)
                }}
                onOk={handleCustomDateRangeApply}
                okText={translate("Toepassen")}
                cancelText={translate("Annuleren")}
            >
                <Form layout="vertical">
                    <Form.Item label={translate("Kies een datumbereik")}>
                        <DatePicker.RangePicker
                            style={{width: '100%'}}
                            format="DD-MM-YYYY"
                            value={customDateRange[0] && customDateRange[1] ? [dayjs(customDateRange[0]), dayjs(customDateRange[1])] : null}
                            onChange={(dates) => {
                                if (dates) {
                                    setCustomDateRange([dates[0], dates[1]])
                                } else {
                                    setCustomDateRange([null, null])
                                }
                            }}
                        />
                    </Form.Item>
                </Form>
            </Modal>

            <h1 className={"garden-title"}>{garden.name} {startDate.year() === endDate.year() ? startDate.year() : `${startDate.year()} - ${endDate.year()}`}</h1>


            {garden.beds.filter(filterBedsOnSearch).map((bed) => {
                const filteredPlantings = bed.plantings.filter((p) => filterPlanting(p, endDate, hideHarvested, search));
                return (
                    <DroppableBed bed={bed} key={`bed${bed.id}`}>
                        <div className={"bed-wrapper"}>
                            <Row>
                            <Col xs={12} md={compactMode ? 2 : 12}>
                                <p className={"bed-title"}>{bed.name} {hideBedDimensions ? null : `(${bedDimensionsToHuman(bed, measurementSystem)})`}</p>
                            </Col>
                            <Col xs={12} md={compactMode ? 0 : 12} className={"bed-buttons"}>
                                {/* TODO MULTI BED */}
                                {/*{bed.mode === "multi" && (*/}
                                {/*    <Button onClick={() => navigate(`/garden/${garden.id}/bed/${bed.id}/multi`)} className={"mar-r-1"} size={compactMode ? 'small' : 'normal'}>*/}
                                {/*        <ArrowsAltOutlined/>*/}
                                {/*    </Button>*/}
                                {/*)}*/}
                                <Button className={"mar-r-1"} onClick={() => openModal(bed)}
                                        size={compactMode ? 'small' : 'normal'}>
                                    <SettingFilled/>
                                </Button>
                                <Button type="default"
                                        size={compactMode ? 'small' : 'normal'}
                                        onClick={() => showPlantingModal(bed)}>
                                    <PlusOutlined/>
                                </Button>
                            </Col>
                            <Col md={compactMode ? 20 : 24}>
                                {filteredPlantings.length === 0 && (
                                    <div className={"add-first-planting-button"}>
                                        <Button style={{margin: '0 8px'}} type="default" size={"small"}
                                                onClick={() => showPlantingModal(bed)}>
                                            <PlusOutlined/> {translate('Eerste gewas toevoegen aan bed')}
                                        </Button>
                                    </div>
                                )}
                                <SortableContext
                                    items={filteredPlantings.map(p => `planting-${p.id}`)}
                                    strategy={verticalListSortingStrategy}
                                >
                                {filteredPlantings.map(planting => (
                                    <SortablePlanting key={`bed-${bed.id}-planting-${planting.id}`} planting={planting} bed={bed}>
                                        <div className={"planting-wrapper"}>
                                            <Row>
                                            <Col xs={4} md={1} onClick={() => navigateToPlanting(bed, planting)}>
                                                <Element name={`planting-${planting.id}`}/>
                                                <CropImage crop={planting.crop} width={compactMode ? '30px' : '48px'}
                                                           height={compactMode ? '30px' : '48px'}/>
                                            </Col>
                                            {amountDisplay !== "none" && (
                                            <Col md={1} xs={3}>
                                                <div className={"planting-name"}>
                                                    {amountDisplay === "total" && <>{planting.plants_per_row * planting.rows}x</>}
                                                    {amountDisplay === "rows_and_plants" && <>{planting.rows}x{planting.plants_per_row}</>}
                                                </div>
                                            </Col>
                                            )}
                                            <Col xs={9} md={5} onClick={() => navigateToPlanting(bed, planting)}>
                                                <div className={"planting-name"}>
                                                    {planting.crop.name}{hideCropVarieties || !planting.crop.variety ? null : ` - ${planting.crop.variety}`}

                                                    {planting.note && compactMode && (
                                                        <i className={"mar-l-1"}>{planting.note}</i>)}
                                                    {planting.crop.note && compactMode && (
                                                        <i className={"mar-l-1"}>{planting.crop.note}</i>)}
                                                    {isNotOnTrack(planting) && (
                                                        <Tooltip title={isNotOnTrack(planting)}>
                                                            <WarningOutlined className={"mar-l-1"}/>
                                                        </Tooltip>
                                                    )}
                                                </div>
                                            </Col>
                                            <Col xs={7} md={2} className={"no-print"}>
                                                <div className={"status-dropdown"}>
                                                    <Select size={"small"} value={planting.status}
                                                            onChange={(e) => {
                                                                api.updatePlanting(planting.id, {
                                                                    ...planting,
                                                                    status: e
                                                                }).then(() => {
                                                                    loadGarden()
                                                                })
                                                            }}
                                                            onClick={(e) => e.preventDefault()}>
                                                        {Object.keys(PLANTING_STATUS_LABELS).filter((s) => {
                                                            if (planting.crop.is_direct_sown === true && s === PLANTING_STATUS.TRANSPLANTED) {
                                                                return false
                                                            }
                                                            return true
                                                        }).map(status => (
                                                            <Select.Option key={status} value={status}>
                                                                {translate(PLANTING_STATUS_LABELS[status])}
                                                            </Select.Option>
                                                        ))}
                                                    </Select>
                                                </div>
                                            </Col>
                                            {(groupByPeriod === "text") && (
                                                <Col md={12} xs={24} className={"text-center"}>
                                                    <Row onClick={() => navigateToPlanting(bed, planting)}>
                                                        <Col span={8} className={"planting-date"}>
                                                            <label>{translate('Zaaien')}</label>
                                                            <div>{dayjs(planting.sow_at).format("DD MMM YYYY")}</div>
                                                        </Col>
                                                        <Col span={8} className={"planting-date"}>
                                                            <label>{translate('Planten')}</label>
                                                            <div>{dayjs(planting.transplant_at).format("DD MMM YYYY")}</div>
                                                        </Col>
                                                        <Col span={8} className={"planting-date"}>
                                                            <label>{translate('Oogsten')}</label>
                                                            <div>{dayjs(planting.harvest_at).format("DD MMM YYYY")}</div>
                                                        </Col>
                                                    </Row>
                                                </Col>
                                            )}
                                            {(periods.length > 0) && (
                                                <Col xs={24} md={15} style={{overflowX: 'auto'}}
                                                     onClick={() => navigateToPlanting(bed, planting)}>
                                                    <div className={"periods-wrapper"}>
                                                        {periods.map(period => (
                                                            <div className={
                                                                inBed(planting, period) ? "period-block period-in-bed" : inSeed(planting, period) ? "period-block period-in-seed" : inHarvest(planting, period) ? "period-block period-in-harvest" : "period-block"
                                                            } key={`${planting.id}-${period.unix()}`}>
                                                                {periodToHuman(period)}
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Col>
                                            )}
                                            {planting.note && !compactMode && (<Col span={24}>
                                                <div className={"mar-t-1"}>
                                                    <i className={"mar-l-1"}>{planting.note}</i>
                                                </div>
                                            </Col>)}
                                            </Row>
                                        </div>
                                    </SortablePlanting>
                                ))}
                                </SortableContext>
                            </Col>
                            <Col xs={0} md={compactMode ? 2 : 0} className={"bed-buttons no-print"}>
                                <Button onClick={() => openModal(bed)} size={compactMode ? 'small' : 'normal'}>
                                    <SettingFilled/>
                                </Button>
                                <Button style={{marginLeft: '8px'}} type="default"
                                        size={compactMode ? 'small' : 'normal'}
                                        onClick={() => showPlantingModal(bed)}>
                                    <PlusOutlined/>
                                </Button>
                            </Col>
                        </Row>
                        </div>
                    </DroppableBed>
                )
            })}

            {garden.beds.length === 0 && (

                <Alert
                    description={translate("Je moestuin heeft nog geen bedden, druk op de onderstaande knop om je eerste bed toe tevoegen!")}></Alert>
            )}

            <div style={{margin: '1rem 0'}}>
                <Button onClick={() => setBedModalOpen(true)} type={"primary"} size={"large"}>
                    {translate('Nieuw bed toevoegen')}
                </Button>
            </div>

            {garden.beds.length === 0 && (
                <>
                    <HelpVideoLoader/>
                </>
            )}

            </div>
            <DragOverlay>
                {activeDragPlanting ? (
                    <div className={"planting-wrapper"} style={{background: 'white', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', borderRadius: '4px', padding: '4px 8px'}}>
                        <Row>
                            <Col>
                                <CropImage crop={activeDragPlanting.crop} width={'30px'} height={'30px'}/>
                            </Col>
                            <Col style={{marginLeft: 8}}>
                                <div className={"planting-name"}>
                                    {activeDragPlanting.crop.name}{activeDragPlanting.crop.variety ? ` - ${activeDragPlanting.crop.variety}` : ''}
                                </div>
                            </Col>
                        </Row>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
    )
}
